import React, { useCallback, useEffect, useState } from 'react';
import {
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    GridItem,
    Input,
    Select,
    Textarea,
    useToast
} from '@chakra-ui/react';
import BraintreeWebClient from 'braintree-web/client';
import BraintreeDataCollector from 'braintree-web/data-collector';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useClientApiFetch } from 'ts/client/common/hooks';
import DataConsentCheckbox from 'ts/common/components/DataConsentCheckbox';
import {
    getFullPaymentAmount,
    getPartialPaymentAmount
} from 'ts/common/components/invoice/helpers';
import { FetchContentType, FetchMethod, useTranslate } from 'ts/common/hooks';
import { useBootstrapData } from 'client_react/bootstrap';
import ClientPaymentForm from 'client_react/common/components/ClientPaymentForm';
import { type ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import Braintree3DSChallenge, { type BraintreeCreditCardInfo } from './Braintree3DSChallenge';
import InvoicePaymentOptions from './InvoicePaymentOptions';
import InvoicePaymentSummary from './InvoicePaymentSummary';
import LegacyCreditCardCVVInput from './LegacyCreditCardCVVInput';
import LegacyCreditCardNumberInput from './LegacyCreditCardNumberInput';

interface FormFields {
    nameOnCard: string;
    cardNumber: string;
    expirationMonth: string;
    expirationYear: number;
    cvvCode: string;
    billingPostalCode: string;
    emailAddress: string;
    note?: string;
    doesAcceptTerms?: boolean;
}

interface CreditCardPaymentData {
    nameOnCard: string;
    cardNumber: string;
    expirationMonth: string;
    expirationYear: number | string;
    cvvCode: string;
    billingPostalCode: string;
    emailAddress: string;
    enableAutoPay: boolean;
    deviceData?: string;
    paymentMethodNonce?: string;
}

interface PaymentResponse {
    info?: {
        transactionErrors?: {
            unexpected?: string;
        };
    };
    type: string;
}

interface Props {
    paymentAmountType: ClientPaymentOption;
    setPaymentAmountType: (x: ClientPaymentOption) => void;
}

const LegacyInvoicePaymentForm: React.FC<Props> = ({ paymentAmountType, setPaymentAmountType }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const t = useTranslate('clientInvoice');
    const { braintreeToken, has3DSEnabled, isSubjectToGdpr } = useBootstrapData();

    const { invoice, refetchInvoice } =
        useOutletContext<{ invoice: SpApi.Client.IInvoice; refetchInvoice: () => Promise<void> }>();

    const [cardType, setCardType] = useState<string | null>(null);
    const [is3DSModalOpen, setIs3DSModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deviceData, setDeviceData] = useState<string | null>(null);

    const form = useForm<FormFields>({ mode: 'onTouched' });

    const { performFetch: createInvoicePayment } = useClientApiFetch<PaymentResponse>(
        `brand/${invoice.brand.id}/invoice/${invoice.publicId}/payment`,
        {
            defer: true,
            headers: {
                'Content-Type': FetchContentType.SpJson
            },
            method: FetchMethod.POST
        }
    );

    const handleClickBack = useCallback(() => {
        navigate(`/invoice/${invoice.publicId}`);
    }, [invoice, navigate]);

    const getExpirationMonths = useCallback(() => {
        return Array.from({ length: 12 }, (_, i) => {
            const month = i + 1;
            return {
                value: month.toString().padStart(2, '0'),
                label: month.toString().padStart(2, '0')
            };
        });
    }, []);

    const getExpirationYears = useCallback(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 15 }, (_, i) => {
            const year = currentYear + i;
            return {
                value: year,
                label: year.toString()
            };
        });
    }, []);

    // Collect device data for Braintree gateways
    useEffect(() => {
        const collectDeviceData = async () => {
            if (!braintreeToken || invoice.paymentGatewayType !== 'braintree') {
                return;
            }

            try {
                const client = await BraintreeWebClient.create({
                    authorization: braintreeToken
                });

                const dataCollector = await BraintreeDataCollector.create({
                    client,
                    kount: true
                });

                setDeviceData(dataCollector.deviceData);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to collect device data:', error);
                // Device data collection failure should not prevent payment submission
            }
        };

        collectDeviceData();
    }, [braintreeToken, invoice]);

    const chargeAmount =
        paymentAmountType === 'full'
            ? getFullPaymentAmount(invoice)
            : getPartialPaymentAmount(invoice);

    const submitPayment = useCallback(
        async (creditCard: CreditCardPaymentData, note?: string) => {
            try {
                const paymentData = {
                    type: 'invoice-payment',
                    amount: chargeAmount,
                    note,
                    creditCard,
                    invoiceId: invoice.publicId
                };

                const response = await createInvoicePayment({
                    body: JSON.stringify(paymentData)
                });

                if (response?.info?.transactionErrors) {
                    const errorMessage =
                        response.info.transactionErrors.unexpected || t('unexpectedError');
                    toast({
                        title: t('error.paymentFailed'),
                        description: errorMessage,
                        status: 'error'
                    });
                    return;
                }

                await refetchInvoice();
                navigate(`/invoice/${invoice.publicId}`);

                toast({
                    title: t('paymentSuccess'),
                    status: 'success'
                });
            } catch (error) {
                toast({
                    title: t('error.paymentFailed'),
                    description: t('unexpectedError'),
                    status: 'error'
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [chargeAmount, invoice, createInvoicePayment, refetchInvoice, navigate, toast, t]
    );

    const handleSubmit = useCallback(
        async (formData: FormFields) => {
            setIsSubmitting(true);

            // If using Braintree with 3DS, perform verification first:
            if (has3DSEnabled) {
                setIs3DSModalOpen(true);
                // The 3DS modal will handle the actual payment submission
                return;
            }

            // Otherwise, do direct payment submission for non-3DS gateways:
            const creditCard = {
                nameOnCard: formData.nameOnCard,
                cardNumber: formData.cardNumber?.replace(/\s/g, ''),
                expirationMonth: formData.expirationMonth,
                expirationYear: formData.expirationYear,
                cvvCode: formData.cvvCode,
                billingPostalCode: formData.billingPostalCode,
                emailAddress: formData.emailAddress,
                enableAutoPay: false,
                ...(deviceData && { deviceData })
            };

            await submitPayment(creditCard, formData.note);
        },
        [has3DSEnabled, deviceData, submitPayment]
    );

    const handle3DSComplete = useCallback(
        async (result: { paymentMethodNonce: string; creditCard: BraintreeCreditCardInfo }) => {
            setIs3DSModalOpen(false);

            const formData = form.getValues();
            const creditCard = {
                nameOnCard: formData.nameOnCard,
                cardNumber: result.creditCard.lastFour || '',
                expirationMonth: result.creditCard.expirationMonth || '',
                expirationYear: result.creditCard.expirationYear || '',
                cvvCode: formData.cvvCode,
                billingPostalCode: formData.billingPostalCode,
                emailAddress: formData.emailAddress,
                enableAutoPay: false,
                paymentMethodNonce: result.paymentMethodNonce,
                ...(deviceData && { deviceData })
            };

            await submitPayment(creditCard, formData.note);
        },
        [form, deviceData, submitPayment]
    );

    const handle3DSClose = useCallback(() => {
        setIsSubmitting(false);
        setIs3DSModalOpen(false);
    }, []);

    const get3DSCreditCardDetails = useCallback(() => {
        const formData = form.getValues();
        return {
            number: formData.cardNumber?.replace(/\s/g, ''),
            cvv: formData.cvvCode,
            expirationDate: `${formData.expirationMonth}/${formData.expirationYear}`,
            billingAddress: {
                postalCode: formData.billingPostalCode
            },
            ...(deviceData && { deviceData })
        };
    }, [form, deviceData]);

    return (
        <FormProvider {...form}>
            <ClientPaymentForm
                backButtonText={t('back')}
                billingInformationFields={
                    <>
                        <FormControl
                            isRequired
                            isInvalid={!!form.formState.errors.nameOnCard}
                            paddingBottom={0}
                        >
                            <FormLabel>{t('nameOnCard')}</FormLabel>
                            <Input {...form.register('nameOnCard', { required: true })} />
                            <FormErrorMessage>
                                {form.formState.errors.nameOnCard?.message}
                            </FormErrorMessage>
                        </FormControl>

                        <FormControl
                            isRequired
                            isInvalid={!!form.formState.errors.cardNumber}
                            paddingBottom={0}
                        >
                            <FormLabel>{t('cardNumber')}</FormLabel>
                            <LegacyCreditCardNumberInput
                                name="cardNumber"
                                onCardTypeChange={setCardType}
                            />
                            <FormErrorMessage>
                                {form.formState.errors.cardNumber?.message}
                            </FormErrorMessage>
                        </FormControl>

                        <Grid templateColumns="1fr 1fr" gap={4}>
                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.expirationMonth}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('expirationMonth')}</FormLabel>
                                    <Select
                                        {...form.register('expirationMonth', {
                                            required: true
                                        })}
                                    >
                                        <option value="">{t('selectMonth')}</option>
                                        {getExpirationMonths().map((month) => (
                                            <option key={month.value} value={month.value}>
                                                {month.label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {form.formState.errors.expirationMonth?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.expirationYear}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('expirationYear')}</FormLabel>
                                    <Select
                                        {...form.register('expirationYear', { required: true })}
                                    >
                                        <option value="">{t('selectYear')}</option>
                                        {getExpirationYears().map((year) => (
                                            <option key={year.value} value={year.value}>
                                                {year.label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {form.formState.errors.expirationYear?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>
                        </Grid>

                        <Grid templateColumns="1fr 1fr" gap={4}>
                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.cvvCode}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('cvvCode')}</FormLabel>
                                    <LegacyCreditCardCVVInput name="cvvCode" cardType={cardType} />
                                    <FormErrorMessage>
                                        {form.formState.errors.cvvCode?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.billingPostalCode}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('billingPostalCode')}</FormLabel>
                                    <Input
                                        {...form.register('billingPostalCode', {
                                            required: true
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {form.formState.errors.billingPostalCode?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>
                        </Grid>

                        <FormControl
                            isRequired
                            isInvalid={!!form.formState.errors.emailAddress}
                            paddingBottom={0}
                        >
                            <FormLabel>{t('email')}</FormLabel>
                            <Input
                                type="email"
                                {...form.register('emailAddress', {
                                    required: true,
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: t('invalidEmail')
                                    }
                                })}
                            />
                            <FormErrorMessage>
                                {form.formState.errors.emailAddress?.message}
                            </FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!form.formState.errors.note} paddingBottom={0}>
                            <FormLabel>{t('note')}</FormLabel>
                            <Textarea
                                {...form.register('note', {
                                    maxLength: {
                                        value: 1000,
                                        message: t('noteTooLong')
                                    }
                                })}
                                maxLength={1000}
                            />
                            <FormErrorMessage>
                                {form.formState.errors.note?.message}
                            </FormErrorMessage>
                        </FormControl>

                        {isSubjectToGdpr && (
                            <DataConsentCheckbox register={form.register} translate={t} />
                        )}
                    </>
                }
                billingInformationTitle={t('billingInformation')}
                isSubmitDisabled={isSubmitting || !form.formState.isValid}
                isSubmitLoading={isSubmitting}
                onClickBack={handleClickBack}
                onSubmit={form.handleSubmit(handleSubmit)}
                paymentOptions={
                    <InvoicePaymentOptions
                        paymentAmountType={paymentAmountType}
                        setPaymentAmountType={setPaymentAmountType}
                    />
                }
                paymentSummary={<InvoicePaymentSummary paymentAmountType={paymentAmountType} />}
                submitButtonText={t('payInvoice')}
            />

            {has3DSEnabled && (
                <Braintree3DSChallenge
                    isOpen={is3DSModalOpen}
                    creditCardDetails={get3DSCreditCardDetails()}
                    chargeAmount={chargeAmount}
                    onComplete={handle3DSComplete}
                    onClose={handle3DSClose}
                />
            )}
        </FormProvider>
    );
};

export default LegacyInvoicePaymentForm;
